import type { ReactNode } from 'react';
import { Controller, useWatch, type Control } from 'react-hook-form';
import { Alert, Input, Label, Radio, DatePicker, Checkbox } from '@shared/components';
import { formatMinorAmount } from '../../../utils/formatScheduleCurrency';
import { dateKeyToLocalDate, toDateKey } from '../../../utils/dateKey';
import { paymentCollectionCopy, type DealerCollectedAxis } from './copy';
import type { DealerCollectedPaymentFormValues } from './dealerCollectedPayment.schema';

interface DealerCollectedPaymentFormProps {
    axis: DealerCollectedAxis;
    control: Control<DealerCollectedPaymentFormValues>;
    /** The offered amount (`useDealerCollectedPayment`), single-sourcing the recorded amount. */
    amountMinor: number;
    /** Renders the dealer radio. When false, only Elemis is offered. */
    isDealerOptionOffered: boolean;
    /** Renders the Elemis radio. When false, only the dealer option is offered. */
    isElemisOptionOffered?: boolean;
    currency: string;
    /** The signed-in user's name, named in the confirmation attestation. */
    userName?: string;
    /** The customer's title, first and last name, named in the confirmation attestation. */
    customerName?: string;
}

const EMPHASIS = 'font-bold italic';

/** The visible attestation and the string form the shared Checkbox needs for its accessible
 *  name — built together so the two can never drift. */
const confirmation = (
    userName: string | undefined,
    amountMinor: number,
    currency: string,
    customerName: string | undefined,
): { label: ReactNode; text: string } => {
    const amount = formatMinorAmount(amountMinor, currency);
    const emphasise = (value: string) => <span className={EMPHASIS}>{value}</span>;
    const opening = userName ? <>I, {emphasise(userName)}, confirm</> : <>I confirm</>;
    const openingText = userName ? `I, ${userName}, confirm` : 'I confirm';
    const customer = customerName ? <>, {emphasise(customerName)}</> : null;
    const customerText = customerName ? `, ${customerName}` : '';

    return {
        label: (
            <>
                {opening} that I have collected a payment of {emphasise(amount)} from the customer
                {customer}.
            </>
        ),
        text: `${openingText} that I have collected a payment of ${amount} from the customer${customerText}.`,
    };
};

/** The amount is read-only — it is `amountMinor`, never a field this form can
 *  disagree with. `control` is owned and validated by the modal's form. */
export function DealerCollectedPaymentForm({
    axis,
    control,
    amountMinor,
    isDealerOptionOffered,
    isElemisOptionOffered = true,
    currency,
    userName,
    customerName,
}: DealerCollectedPaymentFormProps) {
    const collectedBy = useWatch({ control, name: 'collectedBy' });
    const isDealerCollected = isDealerOptionOffered && collectedBy === 'dealer';
    const copy = paymentCollectionCopy({ kind: 'radio', axis });
    const attestation = confirmation(userName, amountMinor, currency, customerName);

    return (
        <fieldset className="space-y-3 rounded-lg border border-mp-border p-4">
            <legend className="px-1 text-sm font-medium text-mp-foreground">{copy.legend}</legend>

            <Controller
                name="collectedBy"
                control={control}
                render={({ field }) => (
                    <div className="flex flex-col gap-2">
                        {isElemisOptionOffered && (
                            <Radio
                                id="dealer-collected-payment-elemis"
                                name="dealer-collected-payment"
                                value="elemis"
                                checked={field.value === 'elemis'}
                                label={copy.elemisLabel}
                                onChange={field.onChange}
                            />
                        )}
                        {isDealerOptionOffered && (
                            <Radio
                                id="dealer-collected-payment-dealer"
                                name="dealer-collected-payment"
                                value="dealer"
                                checked={field.value === 'dealer'}
                                label={copy.dealerLabel}
                                onChange={field.onChange}
                            />
                        )}
                    </div>
                )}
            />

            {isDealerCollected && (
                <div className="space-y-4 pt-2" data-testid="dealer-collected-payment-fields">
                    <Alert variant="success" role="note">
                        <p className="font-medium">
                            Amount to be collected: <span className="font-bold">{formatMinorAmount(amountMinor, currency)}</span>
                        </p>
                    </Alert>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <Label htmlFor="dealer-collected-payment-reference" required>
                                Cash Sale Invoice Number
                            </Label>
                            <Controller
                                name="reference"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <Input
                                        id="dealer-collected-payment-reference"
                                        type="text"
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        error={!!error}
                                        hint={error?.message}
                                        hintId="dealer-collected-payment-reference-hint"
                                        aria-invalid={!!error}
                                        aria-describedby={
                                            error ? 'dealer-collected-payment-reference-hint' : undefined
                                        }
                                    />
                                )}
                            />
                        </div>

                        <div>
                            <Label htmlFor="dealer-collected-payment-collected-at" required>
                                Date collected
                            </Label>
                            <Controller
                                name="collectedAt"
                                control={control}
                                render={({ field, fieldState: { error } }) => (
                                    <DatePicker
                                        id="dealer-collected-payment-collected-at"
                                        value={dateKeyToLocalDate(field.value) ?? null}
                                        onChange={(date) => field.onChange(date ? (toDateKey(date) ?? '') : '')}
                                        maxDate="today"
                                        error={!!error}
                                        hint={error?.message}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <Controller
                        name="confirmed"
                        control={control}
                        render={({ field }) => (
                            <Checkbox
                                id="dealer-collected-payment-confirmed"
                                checked={field.value}
                                onChange={field.onChange}
                                label={attestation.label}
                                ariaLabel={attestation.text}
                            />
                        )}
                    />
                </div>
            )}
        </fieldset>
    );
}
